import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Offer, SmartProduct } from '../../types';
import ProductOffer from '../ProductOffer';
import { SkeletonOffer } from '../elements';
import { SmartUpsellProductOfferProps } from './SmartUpsellProductOfferProps';
import {
  distributeOffersToSmartProducts,
  getCart,
  getRecommendedProductsBase,
} from '../../shared/api/shared-app-requests';
import {
  IGNORE_TAGS,
  RecommendedProductsRequestConfig,
} from '../../shared/api/types';
import { cleanupStaleAddedRecommendedProducts } from '../../utils/candycart/products';
import { useSharedContext } from 'src/contexts/SharedContext';
import {
  trackCheckout,
  trackSmartOffersView,
} from '../../utils/tracking-utils-candyrack';
import { REGISTERED_APPS } from 'src/shared/types';

const SmartUpsellProductOffer = ({
  smartOffers,
  offers,
  parentProduct,
  order,
  offerCount,
  ignoreTag,
  productId,
  recommendedProductsRequestConfig,
  ...restProps
}: SmartUpsellProductOfferProps & {
  productId: number;
  ignoreTag: IGNORE_TAGS;
  recommendedProductsRequestConfig: RecommendedProductsRequestConfig;
}) => {
  const [recommendedProducts, setRecommendedProducts] =
    useState<SmartProduct[]>();
  const [shouldClosePopup, setShouldClosePopupPopup] = useState<boolean>(false);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);
  const { runningAppName, storefrontAccessToken, useDSRecommEngine } =
    useSharedContext();

  let limit = useMemo(
    () =>
      smartOffers.reduce((acc: number, curr: Offer) => {
        return acc + curr.recommended_product_count;
      }, 0),
    [smartOffers],
  );

  limit = limit > 10 ? 10 : limit;

  useEffect(() => {
    async function fetchAndDoStuffWithData() {
      const recommendedProducts = await getRecommendedProductsBase(
        productId,
        recommendedProductsRequestConfig,
        ignoreTag,
        10,
        storefrontAccessToken,
        useDSRecommEngine,
        runningAppName,
      );
      const { items } = await getCart();
      // Parent product is undefined for CC
      if (parentProduct) {
        let filteredRecommendedProducts = recommendedProducts
          .filter((product: any) => {
            const isNotSameHandle = !offers.some((offer) =>
              offer.items.some((item) => product.handle === item.item_handle),
            );
            const isNotSameAsParent = product.handle !== parentProduct.handle;
            const isNotAlreadyInCart = !items.find(
              (item) => product.handle === item.handle,
            );
            return isNotSameHandle && isNotSameAsParent && isNotAlreadyInCart;
          })
          .slice(0, limit);
        filteredRecommendedProducts = distributeOffersToSmartProducts(
          filteredRecommendedProducts,
          smartOffers,
        );

        setRecommendedProducts(filteredRecommendedProducts);
      } else {
        // recommended products without ones already offered by ProductOffers and cart items

        let filteredRecommendedProducts = recommendedProducts
          .filter((product: any) => {
            const isNotSameHandle = !offers.some((offer) =>
              offer.items.some((item) => product.handle === item.item_handle),
            );
            const isNotAlreadyInCart = !items.find(
              (item) => product.handle === item.handle,
            );
            return isNotSameHandle && isNotAlreadyInCart;
          })
          .slice(0, limit);

        filteredRecommendedProducts = distributeOffersToSmartProducts(
          filteredRecommendedProducts,
          smartOffers,
        );

        // Removing potentially non existing offers
        const recommendedVariantIds = filteredRecommendedProducts.flatMap(
          (product: any) => product.variants.map((variant: any) => variant.id),
        );

        smartOffers.forEach((offer) => {
          cleanupStaleAddedRecommendedProducts(offer.id, recommendedVariantIds);
        });

        setRecommendedProducts(filteredRecommendedProducts);
      }
    }
    fetchAndDoStuffWithData();
  }, [
    ignoreTag,
    limit,
    offers,
    parentProduct,
    productId,
    recommendedProductsRequestConfig,
    smartOffers,
    storefrontAccessToken,
    useDSRecommEngine,
    runningAppName,
  ]);

  // Track smart offers when recommended products are loaded
  useEffect(() => {
    if (recommendedProducts && recommendedProducts.length > 0) {
      const offersWithProducts = smartOffers?.map((offer) => ({
        id: offer.id,
        product_ids: recommendedProducts
          ?.filter((product) => product?.offer?.id === offer?.id)
          ?.map((product) => product.id),
      }));
      if (runningAppName === REGISTERED_APPS.ADD_TO_CART) {
        trackSmartOffersView(offersWithProducts);
        return;
      }
      if (runningAppName === REGISTERED_APPS.CHECKOUT_BUTTON) {
        trackCheckout(offersWithProducts);
        return;
      }
    }
  }, [recommendedProducts, runningAppName, smartOffers]);

  const handleOnProductOfferReady = useCallback(() => {
    setShowSkeleton(false);
  }, []);

  if (
    recommendedProducts?.length === 0 &&
    offers.length === smartOffers.length &&
    !shouldClosePopup
  ) {
    setShouldClosePopupPopup(true);
    restProps.onClose();
  }

  const skeletonCount = Array.from({ length: limit }, (v, k) => k + 1);

  return (
    <>
      {showSkeleton &&
        skeletonCount.map((key) => (
          <SkeletonOffer
            indexInOffers={order}
            key={key}
            offerCount={offerCount}
          />
        ))}
      {recommendedProducts &&
        recommendedProducts.map((product) => (
          <ProductOffer
            key={product.id}
            indexInOffers={order}
            offerCount={offerCount}
            offer={product.offer}
            parentProduct={parentProduct}
            currentProduct={product}
            onReady={handleOnProductOfferReady}
            {...restProps}
          />
        ))}
    </>
  );
};

export default SmartUpsellProductOffer;
